import type { GetOrganizationResponseBodyType } from "@intelligems/ig-types";
import { useEffect } from "react";

import { IgStorage } from "@src/utils/igStorage";

import { useMutateChargeId } from "../api/organizations/mutate-charge-id";
import { useUser } from "../api/user-auths/login";
import { message } from "../utils/message";

// AppSubscriptions created before the billing deep link existed carry the bare
// app root as their Shopify return URL, and Shopify accepts no return URL at
// all on a cap raise, so those approvals land the merchant here instead of on
// the billing page. Forwarding keeps the marker in the query: the root only
// routes, and the billing page applies the full arming guard.
const forwardShopifyApprovalReturn = (
  org: GetOrganizationResponseBodyType["org"],
  code: string | undefined
): void => {
  if (
    window.location.pathname !== "/" ||
    org.billingRegime !== "modular_2026" ||
    // An OAuth install callback carries `code` and is the legacy install flow,
    // not a return from an approval the merchant was asked for.
    code !== undefined
  ) {
    return;
  }
  window.location.assign(`/settings${window.location.search}#billing`);
};

export const useAuth = (enabled = true) => {
  const mutateChargeId = useMutateChargeId();
  const user = useUser({ enabled });

  useEffect(() => {
    if (user.isSuccess) {
      if (user.data.type === "redirect") {
        window.location.href = user.data.redirect + "&src=useAuthRedirect";
      } else if (user.data.type === "clear") {
        IgStorage.clearAuthTokens();
        window.location.reload();
      } else if (user.data.token) {
        const search = window.location.search;
        const params = new URLSearchParams(search);
        const charge_id = params.get("charge_id") || undefined;
        const code = params.get("code") || undefined;
        if (charge_id !== undefined) {
          mutateChargeId.mutate(
            { chargeId: charge_id, code },
            { onSuccess: ({ org }) => forwardShopifyApprovalReturn(org, code) }
          );
        }
      }
    } else if (user.fetchStatus === "idle") {
      // This fixes the forever loading issue
      message({ type: "NOT_LOGGED_IN", payload: "user.isIdle" });
    }
  }, [user.isLoading, user.data, user.isSuccess]);

  return user;
};
